import React from 'react';

import { getApplicableL2CompanyModules } from 'Deer/components/navigation/secondary/Company/CompanyModules';
import { L2_PATHS, staticRoutes } from 'Deer/constants/routes';
import { getUserAccess, getUserL2Access } from 'Deer/helpers/getUserAccess';
import PrivateRoute from 'components/PrivateRoute';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { Redirect, Route, Switch } from 'react-router-dom';
import {
  checkCrossModularInsightsEnabled,
  checkVisierAnalyticsEnabled,
  checkLifecycleInsightsEnabled,
} from 'utils/helpers/featureFlags';

import { PageNotFound404 } from '../PageNotFound404';
import { EmbeddedReport } from './Analytics';
import CompanyLifecycle from './Analytics/Lifecycle';
import { OutcomesDashboard } from './Analytics/OutcomesDashboard';
import { CompanyOverview } from './CompanyOverview';
import CompanyDevelop from './Develop';
import CompanyDisciplinaryActions from './DisciplinaryActions';
import CompanyEngagement from './Engagement';
import CompanyGoals from './Goals';
import CompanyOneOnOne from './OneOnOne';
import CompanyReviews from './Reviews';
import CompetenciesSettings from './Settings/Competencies';
import PlatformSettings from './Settings/Platform';
import PluginSettings from './Settings/Plugins';

const Company = () => {
  const {
    hasGoalAccess,
    hasFeedbackAccess,
    hasOneOnOneAccess,
    hasReviewAccess,
    hasEngagementAccess,
    hasDisciplinaryActionsAccess,
  } = getUserL2Access();
  const {
    isEngagementAdmin,
    isDisciplinaryActionsAdmin,
    isPlatformAdmin,
    isFeedbackAdmin,
    isGoalAdmin,
    isOneOnOneAdmin,
    isReviewAdmin,
    isCustomGoalAdmin,
    isCustomEngagementAdmin,
    isCustomDisciplinaryActionsAdmin,
    isCustomFeedbackAdmin,
    isModuleAdmin,
    isCustomOneOnOneAdmin,
    isCustomReviewAdmin,
  } = getUserAccess();

  const { moduleName } = useOrganisationSettings();

  const applicableL2CompanyModules = getApplicableL2CompanyModules(moduleName);

  return (
    <Switch>
      {hasGoalAccess && (isGoalAdmin || isCustomGoalAdmin) ? (
        <PrivateRoute path={L2_PATHS.company.goal} component={CompanyGoals} />
      ) : null}
      {hasFeedbackAccess && (isFeedbackAdmin || isCustomFeedbackAdmin) ? (
        <PrivateRoute path={L2_PATHS.company.develop} component={CompanyDevelop} />
      ) : null}
      {hasOneOnOneAccess && (isOneOnOneAdmin || isCustomOneOnOneAdmin) ? (
        <PrivateRoute path={L2_PATHS.company.oneOnOne} component={CompanyOneOnOne} />
      ) : null}
      {hasEngagementAccess && (isEngagementAdmin || isCustomEngagementAdmin) ? (
        <PrivateRoute path={L2_PATHS.company.engagement} component={CompanyEngagement} />
      ) : null}
      {hasDisciplinaryActionsAccess && (isDisciplinaryActionsAdmin || isCustomDisciplinaryActionsAdmin) ? (
        <PrivateRoute path={L2_PATHS.company.disciplinaryActions} component={CompanyDisciplinaryActions} />
      ) : null}
      {hasReviewAccess && (isReviewAdmin || isCustomReviewAdmin) ? (
        <PrivateRoute path={L2_PATHS.company.review} component={CompanyReviews} />
      ) : null}
      {isPlatformAdmin ? (
        <PrivateRoute path={staticRoutes.company.settings.plugins.index} component={PluginSettings} />
      ) : null}
      {isPlatformAdmin ? (
        <PrivateRoute path={staticRoutes.company.settings.platform.index} component={PlatformSettings} />
      ) : null}

      {isPlatformAdmin ? (
        <PrivateRoute path={staticRoutes.company.settings.competencies} component={CompetenciesSettings} />
      ) : null}

      {(isPlatformAdmin || isModuleAdmin) && checkVisierAnalyticsEnabled() ? (
        <PrivateRoute path={L2_PATHS.company.overview} component={CompanyOverview} />
      ) : null}

      {checkVisierAnalyticsEnabled() && isModuleAdmin ? (
        <PrivateRoute path={staticRoutes.company.analytics.reports} component={EmbeddedReport} />
      ) : null}

      {checkVisierAnalyticsEnabled() && isModuleAdmin && checkCrossModularInsightsEnabled() ? (
        <PrivateRoute path={staticRoutes.company.analytics.outcomesDashboard} component={OutcomesDashboard} />
      ) : null}

      {checkLifecycleInsightsEnabled() ? (
        <PrivateRoute path={L2_PATHS.company.lifecycle} component={CompanyLifecycle} />
      ) : null}
      {/* Redirecting partial company  paths to first applicable L2 path */}
      {applicableL2CompanyModules.length > 0 && (
        <PrivateRoute exact path="/app/company">
          <Redirect to={applicableL2CompanyModules[0].value} />
        </PrivateRoute>
      )}
      <Route path="*" component={PageNotFound404} />
    </Switch>
  );
};

export default Company;
